/**
 * This is a custom React hook that checks if a given media query matches the current window size and
 * returns a boolean value.
 * @param mediaQuery - The media query string that will be used to determine if the current screen
 * matches the specified conditions. It can be any valid CSS media query, such as "screen and
 * (max-width: 768px)" to check if the screen width is less than or equal to 768 pixels.
 * @returns The `useMediaQuery` custom hook returns a boolean value indicating whether the specified
 * media query matches the current viewport size.
 */

import { useEffect, useState } from 'react';

export const useMediaQuery = (mediaQuery) => {
  const query = mediaQuery
  const [isVerified, setIsVerified] = useState(
    typeof window === 'object' && !!window.matchMedia(query).matches
  );

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const documentChangeHandler = () => setIsVerified(!!mediaQueryList.matches);

    try {
      mediaQueryList.addEventListener('change', documentChangeHandler);
    } catch (e) {
      mediaQueryList.addListener(documentChangeHandler);
    }

    documentChangeHandler();
    return () => {
      try {
        mediaQueryList.removeEventListener('change', documentChangeHandler);
      } catch (e) {
        mediaQueryList.removeListener(documentChangeHandler);
      }
    };
  }, [query]);

  return isVerified;
};
